import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Centrifuge } from 'centrifuge';
import { api } from './client';
import {
  achievementCategorySchema,
  achievementDetailSchema,
  achievementsSummarySchema,
  appStatusSchema,
  avatarUploadSchema,
  celebrationSchema,
  championCardSchema,
  hallSeasonSchema,
  showcaseItemSchema,
  homeSchema,
  leaderboardSchema,
  liveSchema,
  myTournamentSchema,
  personalLeaderboardSchema,
  personalRatingSchema,
  profileSchema,
  publicProfileSchema,
  qualArchiveSchema,
  qualCelebrationSchema,
  qualPositionSchema,
  qualificationOverviewSchema,
  ratingCelebrationSchema,
  registrationsSchema,
  seasonSchema,
  tournamentResultSchema,
  tournamentSchema,
  type NotificationPrefs,
  passOverviewSchema,
  passQuestDetailSchema,
  passDecksSchema,
  passOpenResultSchema,
  passChancesSchema,
  passShopSchema,
  passPrizesSchema,
  passWardrobeSchema,
  passVoucherSchema,
  passOrderSchema,
  passOrderRawSchema,
  passFreezesSchema,
  seasonRecapSchema,
  kgdOverviewSchema,
  kgdPositionSchema,
  kgdCelebrationSchema,
  kgdRulesSchema,
} from './types';
import { z } from 'zod';

export function useHome() {
  return useQuery({ queryKey: ['home'], queryFn: () => api.get('/home', homeSchema) });
}

const flagsSchema = z.record(z.string(), z.boolean());

/**
 * Фича-флаги (раскатка на часть игроков, правит CRM → «Фичи»). Пока флаги не
 * загружены — фича считается выключенной (не мигает лишними карточками).
 */
export function useFlags() {
  return useQuery({
    queryKey: ['flags'],
    queryFn: () => api.get('/me/flags', flagsSchema),
    staleTime: 60_000,
  });
}

export function useFlag(key: string): boolean {
  const flags = useFlags();
  return Boolean(flags.data?.[key]);
}

/**
 * Public app status (maintenance flag). In normal operation it's fetched once on
 * load + on window focus (no constant polling — the 503 interceptor carries the
 * signal on real traffic). While paused, `poll` turns on a 20s refetch so users
 * recover automatically when maintenance ends. Fail-open (no retry).
 */
export function useAppStatus(poll: boolean) {
  return useQuery({
    queryKey: ['app-status'],
    queryFn: () => api.get('/status', appStatusSchema),
    refetchInterval: poll ? 20_000 : false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
    retry: false,
  });
}

export function useSeasons() {
  return useQuery({
    queryKey: ['seasons'],
    queryFn: () => api.get('/seasons', z.array(seasonSchema)),
    staleTime: 5 * 60 * 1000,
  });
}

/** «Зал славы»: сезоны с чемпионами (метрики живые из лидерборда). */
export function useHallOfFame() {
  return useQuery({
    queryKey: ['hall-of-fame'],
    queryFn: () => api.get('/seasons/champions', z.array(hallSeasonSchema)),
    staleTime: 5 * 60 * 1000,
  });
}

/** Полная карточка чемпиона сезона; 404 для активного сезона. */
export function useSeasonChampion(seasonId: number) {
  return useQuery({
    queryKey: ['season-champion', seasonId],
    queryFn: () => api.get(`/seasons/${seasonId}/champion`, championCardSchema),
    enabled: Number.isFinite(seasonId) && seasonId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLeaderboard(seasonId: number | null) {
  return useQuery({
    queryKey: ['leaderboard', seasonId],
    queryFn: () =>
      api.get(`/leaderboard${seasonId ? `?seasonId=${seasonId}` : ''}`, leaderboardSchema),
  });
}

export function useProfile(seasonId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['profile', seasonId],
    queryFn: () => api.get(`/me${seasonId ? `?seasonId=${seasonId}` : ''}`, profileSchema),
    enabled,
  });
}

export function useMyTournaments(enabled = true) {
  return useQuery({
    queryKey: ['my-tournaments'],
    queryFn: () => api.get('/me/tournaments', z.array(myTournamentSchema)),
    enabled,
  });
}

export function usePublicProfile(id: number) {
  return useQuery({
    queryKey: ['player', id],
    queryFn: () => api.get(`/players/${id}`, publicProfileSchema),
    enabled: Number.isFinite(id),
  });
}

export function usePlayerTournaments(id: number) {
  return useQuery({
    queryKey: ['player-tournaments', id],
    queryFn: () => api.get(`/players/${id}/tournaments`, z.array(myTournamentSchema)),
    enabled: Number.isFinite(id),
  });
}

export function useTournamentResults(id: number) {
  return useQuery({
    queryKey: ['tournament-results', id],
    queryFn: () => api.get(`/tournaments/${id}/results`, tournamentResultSchema),
    enabled: Number.isFinite(id),
  });
}

const liveTokenSchema = z.object({
  wsUrl: z.string().nullable(),
  token: z.string().nullable(),
  channel: z.string(),
  playerId: z.number(),
});

/**
 * Live tournament state. Primary delivery is Centrifugo WS push (subscribe to
 * `tournament:{id}`); if Centrifugo isn't configured or the socket is down we
 * fall back to 2s polling. The clock still ticks locally from `levelEndsAt`.
 */
export function useTournamentLive(id: number) {
  const qc = useQueryClient();
  const [wsConnected, setWsConnected] = useState(false);

  const query = useQuery({
    queryKey: ['tournament-live', id],
    queryFn: () => api.get(`/tournaments/${id}/live`, liveSchema),
    enabled: Number.isFinite(id),
    // Poll only while the push channel is down (graceful fallback).
    refetchInterval: wsConnected ? false : 2000,
  });

  useEffect(() => {
    if (!Number.isFinite(id)) return;
    let cf: Centrifuge | null = null;
    let cancelled = false;
    (async () => {
      try {
        const info = await api.get(`/tournaments/${id}/live/token`, liveTokenSchema);
        if (cancelled || !info.wsUrl || !info.token) return; // not configured ⇒ keep polling
        cf = new Centrifuge(info.wsUrl, { token: info.token });
        const sub = cf.newSubscription(info.channel);
        sub.on('publication', (ctx) => {
          const parsed = liveSchema.safeParse(ctx.data);
          if (!parsed.success) return;
          const dto = parsed.data;
          // isMe is per-viewer (stripped from the shared push) — stamp it here.
          qc.setQueryData(['tournament-live', id], {
            ...dto,
            players: dto.players.map((p) => ({ ...p, isMe: p.playerId === info.playerId })),
          });
        });
        cf.on('connected', () => setWsConnected(true));
        cf.on('disconnected', () => setWsConnected(false));
        sub.subscribe();
        cf.connect();
      } catch {
        /* token fetch failed / Centrifugo down ⇒ polling fallback stays active */
      }
    })();
    return () => {
      cancelled = true;
      setWsConnected(false);
      cf?.disconnect();
    };
  }, [id, qc]);

  return query;
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: { displayName?: string | null; email?: string | null }) =>
      api.patch('/me', profileSchema, patch),
    onSuccess: (data) => {
      qc.setQueryData(['profile', null], data);
      void qc.invalidateQueries({ queryKey: ['profile'] });
      void qc.invalidateQueries({ queryKey: ['leaderboard'] });
    },
  });
}

export function useUpdateNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: NotificationPrefs) => api.put('/me/notifications', profileSchema, prefs),
    onSuccess: (data) => {
      qc.setQueryData(['profile', null], data);
      void qc.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload('/me/avatar', avatarUploadSchema, fd);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useTournaments(status: 'upcoming' | 'past') {
  return useQuery({
    queryKey: ['tournaments', status],
    queryFn: () => api.get(`/tournaments?status=${status}`, z.array(tournamentSchema)),
  });
}

export function useTournament(id: number) {
  return useQuery({
    queryKey: ['tournament', id],
    queryFn: () => api.get(`/tournaments/${id}`, tournamentSchema),
    enabled: Number.isFinite(id),
  });
}

export function useRegistrations(id: number) {
  return useQuery({
    queryKey: ['registrations', id],
    queryFn: () => api.get(`/tournaments/${id}/registrations`, registrationsSchema),
    enabled: Number.isFinite(id),
  });
}

export function useRegister(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/tournaments/${id}/register`, tournamentSchema),
    onSuccess: (data) => {
      qc.setQueryData(['tournament', id], data);
      void qc.invalidateQueries({ queryKey: ['tournaments'] });
      void qc.invalidateQueries({ queryKey: ['registrations', id] });
      void qc.invalidateQueries({ queryKey: ['home'] });
      void qc.invalidateQueries({ queryKey: ['qualification'] });
      void qc.invalidateQueries({ queryKey: ['qualification-me'] });
      void qc.invalidateQueries({ queryKey: ['kaliningrad'] });
      void qc.invalidateQueries({ queryKey: ['kaliningrad-me'] });
      // Задание пасса выдаётся при записи асинхронно — подтянуть чуть позже.
      void qc.invalidateQueries({ queryKey: ['pass'] });
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['tournament', id] });
        void qc.invalidateQueries({ queryKey: ['pass'] });
      }, 1500);
    },
  });
}

export function useCancelRegistration(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del(`/tournaments/${id}/register`, tournamentSchema),
    onSuccess: (data) => {
      qc.setQueryData(['tournament', id], data);
      void qc.invalidateQueries({ queryKey: ['tournaments'] });
      void qc.invalidateQueries({ queryKey: ['registrations', id] });
      void qc.invalidateQueries({ queryKey: ['home'] });
      void qc.invalidateQueries({ queryKey: ['qualification'] });
      void qc.invalidateQueries({ queryKey: ['qualification-me'] });
      void qc.invalidateQueries({ queryKey: ['kaliningrad'] });
      void qc.invalidateQueries({ queryKey: ['kaliningrad-me'] });
      void qc.invalidateQueries({ queryKey: ['pass'] });
    },
  });
}

/* ── Достижения ── */

/** Сводка K1/PF1: свой каталог (pid=null) или гостевой (pid игрока). */
export function useAchievements(pid: number | null) {
  return useQuery({
    queryKey: ['achievements', pid],
    queryFn: () =>
      api.get(pid === null ? '/achievements' : `/players/${pid}/achievements`, achievementsSummarySchema),
    staleTime: 60 * 1000,
  });
}

export function useAchievementCategory(pid: number | null, categoryId: string) {
  return useQuery({
    queryKey: ['achievement-category', pid, categoryId],
    queryFn: () =>
      api.get(
        pid === null
          ? `/achievements/categories/${categoryId}`
          : `/players/${pid}/achievements/categories/${categoryId}`,
        achievementCategorySchema,
      ),
    enabled: categoryId.length > 0,
  });
}

export function useAchievementDetail(pid: number | null, id: number) {
  return useQuery({
    queryKey: ['achievement', pid, id],
    queryFn: () =>
      api.get(
        pid === null ? `/achievements/${id}` : `/players/${pid}/achievements/${id}`,
        achievementDetailSchema,
      ),
    enabled: Number.isFinite(id) && id > 0,
  });
}

/** Полученные — сетка редактора витрины (V1). */
export function useUnlockedAchievements(enabled: boolean) {
  return useQuery({
    queryKey: ['achievements-unlocked'],
    queryFn: () => api.get('/achievements/unlocked', z.array(showcaseItemSchema)),
    enabled,
  });
}

export function useUpdateShowcase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (achievementIds: number[]) =>
      api.put('/me/achievements/showcase', z.array(showcaseItemSchema), { achievementIds }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['achievements', null] });
    },
  });
}

/** Празднования C1: непоказанные уровни; запрашивается один раз на старте. */
export function useCelebrations(enabled: boolean) {
  return useQuery({
    queryKey: ['celebrations'],
    queryFn: () => api.get('/me/achievements/celebrations', z.array(celebrationSchema)),
    enabled,
    staleTime: Infinity,
  });
}

export function useAckCelebration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { achievementId: number; level: number }) =>
      api.post('/me/achievements/celebrations/ack', z.object({ ok: z.literal(true) }), payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['achievements', null] });
    },
  });
}

/* ── Личный рейтинг ── */

/** Свой кабинет (pid=null) или гостевая карточка R2-4 (pid игрока). */
export function usePersonalRating(pid: number | null) {
  return useQuery({
    queryKey: ['personal-rating', pid],
    queryFn: () =>
      api.get(pid === null ? '/me/rating' : `/players/${pid}/rating`, personalRatingSchema),
    staleTime: 60 * 1000,
  });
}

/** Лидерборд «Личный рейтинг» (RC1). */
export function usePersonalLeaderboard(enabled = true) {
  return useQuery({
    queryKey: ['personal-leaderboard'],
    queryFn: () => api.get('/leaderboard/personal', personalLeaderboardSchema),
    enabled,
    staleTime: 60 * 1000,
  });
}

/** RE2: непоказанное повышение уровня; запрашивается один раз на старте. */
export function useRatingCelebration(enabled: boolean) {
  return useQuery({
    queryKey: ['rating-celebration'],
    queryFn: () => api.get('/me/rating/celebration', ratingCelebrationSchema),
    enabled,
    staleTime: Infinity,
  });
}

export function useAckRatingCelebration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/me/rating/celebration/ack', z.object({ ok: z.literal(true) })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['personal-rating', null] });
    },
  });
}

/* ── Отбор на Чемпионат ── */

export function useQualification() {
  return useQuery({
    queryKey: ['qualification'],
    queryFn: () => api.get('/qualification', qualificationOverviewSchema),
    staleTime: 30 * 1000,
  });
}

export function useQualificationMe(enabled = true) {
  return useQuery({
    queryKey: ['qualification-me'],
    queryFn: () => api.get('/qualification/me', qualPositionSchema),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useQualificationPlayer(id: number) {
  return useQuery({
    queryKey: ['qualification-player', id],
    queryFn: () => api.get(`/qualification/players/${id}`, qualPositionSchema),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 30 * 1000,
  });
}

export function useQualificationArchive(enabled: boolean) {
  return useQuery({
    queryKey: ['qualification-archive'],
    queryFn: () => api.get('/qualification/archive', qualArchiveSchema),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Рекап пост-гейм (Q3): непоказанный итог последней игры отбора. */
export function useQualificationCelebration(enabled: boolean) {
  return useQuery({
    queryKey: ['qualification-celebration'],
    queryFn: () => api.get('/qualification/celebration', qualCelebrationSchema),
    enabled,
    staleTime: Infinity,
  });
}

export function useAckQualificationCelebration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/qualification/celebration/ack', z.object({ ok: z.literal(true) })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['qualification'] });
      void qc.invalidateQueries({ queryKey: ['qualification-me'] });
    },
  });
}

/* ── Straddle Pass ── */

export function usePass(enabled = true) {
  return useQuery({
    queryKey: ['pass'],
    queryFn: () => api.get('/pass', passOverviewSchema),
    enabled,
  });
}

/** 6.1d — экран задания. */
export function usePassQuest(id: number, enabled = true) {
  return useQuery({
    queryKey: ['pass-quest', id],
    queryFn: () => api.get(`/pass/quests/${id}`, passQuestDetailSchema),
    enabled: enabled && Number.isFinite(id),
  });
}

export function usePassDecks(enabled = true) {
  return useQuery({
    queryKey: ['pass', 'decks'],
    queryFn: () => api.get('/pass/decks', passDecksSchema),
    enabled,
  });
}

export function useOpenDeck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deckId: number) =>
      api.post(`/pass/decks/${deckId}/open`, passOpenResultSchema, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pass'] }),
  });
}

export function usePassClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { track: 'free' | 'plus'; level: number }) =>
      api.post('/pass/claim', z.object({ ok: z.boolean() }), v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pass'] }),
  });
}

export function usePassClaimAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/pass/claim-all', z.object({ claimed: z.number() }), {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pass'] }),
  });
}

export function usePassChances(enabled = true) {
  return useQuery({
    queryKey: ['pass', 'chances'],
    queryFn: () => api.get('/pass/chances', passChancesSchema),
    enabled,
  });
}

export function usePassShop(enabled = true) {
  return useQuery({
    queryKey: ['pass', 'shop'],
    queryFn: () => api.get('/pass/shop', passShopSchema),
    enabled,
  });
}

export function usePassPrizes(rarity: number | null) {
  return useQuery({
    queryKey: ['pass', 'prizes', rarity],
    queryFn: () => api.get(`/pass/shop/prizes/${rarity}`, passPrizesSchema),
    enabled: rarity !== null,
  });
}

export function usePassWardrobe(enabled = true) {
  return useQuery({
    queryKey: ['pass', 'wardrobe'],
    queryFn: () => api.get('/pass/wardrobe', passWardrobeSchema),
    enabled,
  });
}

export function usePassEquip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { slot: string; itemId: number | null }) =>
      api.post('/pass/wardrobe/equip', z.object({ ok: z.boolean() }), v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pass', 'wardrobe'] });
      // Косметика видна в профиле/списках — обновим и их.
      void qc.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function usePassSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId?: number) =>
      api.post('/pass/wardrobe/seen', z.object({ ok: z.boolean() }), itemId ? { itemId } : {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pass', 'wardrobe'] }),
  });
}

export function usePassVouchers(enabled = true) {
  return useQuery({
    queryKey: ['pass', 'vouchers'],
    queryFn: () => api.get('/pass/vouchers', z.array(passVoucherSchema)),
    enabled,
  });
}

export function usePassOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      kind: 'plus' | 'decks';
      deckRarity?: number;
      qty?: number;
      provider?: 'desk' | 'stars';
    }) => api.post('/pass/orders', passOrderRawSchema, v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pass', 'order-active'] }),
  });
}

/** Инвойс-ссылка Stars для pending-заказа (можно запрашивать повторно). */
export function usePassOrderInvoice() {
  return useMutation({
    mutationFn: (code: string) =>
      api.post(`/pass/orders/${encodeURIComponent(code)}/invoice`, z.object({ link: z.string() })),
  });
}

export function usePassCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api.post(`/pass/orders/${encodeURIComponent(code)}/cancel`, z.object({ ok: z.boolean() })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pass', 'order-active'] }),
  });
}

/** Активный заказ: пока открыт лист с кодом — поллинг статуса (оплату видно сразу). */
export function usePassActiveOrder(enabled = true, poll = false) {
  return useQuery({
    queryKey: ['pass', 'order-active'],
    queryFn: () => api.get('/pass/orders/active', z.object({ order: passOrderSchema.nullable() })),
    enabled,
    refetchInterval: poll ? 4000 : false,
  });
}

/** Онбординг-сторис закрыт — вотермарк на бэке (повторно не показываем). */
export function useMarkIntroSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/me/intro-seen', profileSchema, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  });
}

export function useAckPlusCelebration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/pass/plus-celebration/ack', z.object({ ok: z.boolean() }), {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pass'] }),
  });
}

/** Тост «Уровень N» показан (вотермарк; кэш не трогаем — тост скрывает клиент). */
export function usePassLevelToastAck() {
  return useMutation({
    mutationFn: () => api.post('/pass/level-toast/ack', z.object({ ok: z.boolean() }), {}),
  });
}

export function usePassFreezes(enabled = true) {
  return useQuery({
    queryKey: ['pass', 'freezes'],
    queryFn: () => api.get('/pass/freezes', passFreezesSchema),
    enabled,
  });
}

export function useActivateWeekFreeze() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/pass/freezes/week', z.object({ ok: z.boolean(), until: z.string() }), {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pass', 'freezes'] });
      void qc.invalidateQueries({ queryKey: ['qualification'] });
    },
  });
}

/** Списать свой ваучер «Бесплатный вход» после записи (Handoff 3, 6.0a). */
export function useUseEntryVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post('/pass/vouchers/use-entry', z.object({ ok: z.boolean(), code: z.string().nullable() }), {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pass', 'vouchers'] }),
  });
}

/** Тост «Заморозка спасла…» показан (вотермарк на тратах). */
export function useFreezeToastAck() {
  return useMutation({
    mutationFn: () => api.post('/pass/freezes/toast-ack', z.object({ ok: z.boolean() }), {}),
  });
}

export function useFreezeAuto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.post('/pass/freezes/auto', z.object({ ok: z.boolean() }), { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pass', 'freezes'] }),
  });
}

export function useSeasonRecap(enabled = true) {
  return useQuery({
    queryKey: ['pass', 'season-recap'],
    queryFn: () => api.get('/pass/season-recap', seasonRecapSchema),
    enabled,
  });
}

export function useAckSeasonRecap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/pass/season-recap/ack', z.object({ ok: z.boolean() }), {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pass', 'season-recap'] }),
  });
}

/* ── Пул-отбор «Калининград» (фичефлаг kaliningrad) ── */

export function useKaliningrad(enabled: boolean) {
  return useQuery({
    queryKey: ['kaliningrad'],
    queryFn: () => api.get('/qualification/kaliningrad', kgdOverviewSchema),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useKaliningradMe(enabled: boolean) {
  return useQuery({
    queryKey: ['kaliningrad-me'],
    queryFn: () => api.get('/qualification/kaliningrad/me', kgdPositionSchema),
    enabled,
    staleTime: 30 * 1000,
  });
}

/** Рекап пост-гейм (экран 3): непоказанный итог последней игры отбора. */
export function useKaliningradCelebration(enabled: boolean) {
  return useQuery({
    queryKey: ['kaliningrad-celebration'],
    queryFn: () => api.get('/qualification/kaliningrad/celebration', kgdCelebrationSchema),
    enabled,
    staleTime: Infinity,
  });
}

export function useAckKaliningradCelebration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post('/qualification/kaliningrad/celebration/ack', z.object({ ok: z.literal(true) })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kaliningrad'] });
      void qc.invalidateQueries({ queryKey: ['kaliningrad-me'] });
    },
  });
}

export function useKaliningradRules(enabled: boolean) {
  return useQuery({
    queryKey: ['kaliningrad-rules'],
    queryFn: () => api.get('/qualification/kaliningrad/rules', kgdRulesSchema),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
